// =============================================================================
// SaaS-Admin > Background jobs
// Live data from GET /saastracker/admin/jobs/status. Read-only.
//
// One place for the support team to see whether the work behind credits is
// healthy: charging from the usage queue, the nightly reconcile (daily summary vs
// UserActions vs credit ledger), the daily UserActions archive to S3, the monthly
// credit history archive to S3, and the price list cache.
// =============================================================================
import React, { useCallback, useEffect, useState } from 'react';
import * as api from '../services/adminDataService';

const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase',
  letterSpacing: '.04em', color: '#6b7684', borderBottom: '1px solid #e3e9ef', fontWeight: 600,
};
const td: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid #eef2f6', fontSize: 13 };
const card: React.CSSProperties = {
  background: '#fff', border: '1px solid #e3e9ef', borderRadius: 10, padding: 18,
};
const rowLabel: React.CSSProperties = { fontSize: 12, color: '#6b7684' };
const rowValue: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#173042', textAlign: 'right' };

const fmtInt = (n: number | null | undefined) => (n ?? 0).toLocaleString();
const fmtNum = (n: number | null | undefined) =>
  (n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

/** API dates are UTC; ones without a zone suffix are treated as UTC too. */
const fmtWhen = (iso: string | null | undefined): string => {
  if (!iso) return 'Never';
  const d = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : iso + 'Z');
  return isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/** Milliseconds of a UTC API date (with or without a zone suffix). */
const toMs = (iso: string): number => new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : iso + 'Z').getTime();

/** 'YYYY-MM-DD' of yesterday (UTC), the day the nightly reconcile checks. */
const yesterdayUtc = (): string => {
  const d = new Date(Date.now() - 86400000);
  return d.toISOString().slice(0, 10);
};

/** 'YYYY-MM-DD' (a UTC day) as e.g. '1 Oct 2026'. */
const fmtDay = (yyyyMmDd: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(yyyyMmDd || '');
  if (!m) return yyyyMmDd || '-';
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const monthLabel = (yyyyMm: string): string => {
  const m = /^(\d{4})-(\d{2})$/.exec(yyyyMm || '');
  return m
    ? new Date(Date.UTC(+m[1], +m[2] - 1, 1)).toLocaleString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' })
    : yyyyMm;
};

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', borderBottom: '1px solid #f1f4f7' }}>
    <span style={rowLabel}>{label}</span>
    <span style={rowValue}>{value}</span>
  </div>
);

const Card: React.FC<{ title: string; icon: string; ok: boolean; children: React.ReactNode }> = ({ title, icon, ok, children }) => (
  <div style={card}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <span className="material-icons" style={{ fontSize: 20, color: ok ? '#1e7e46' : '#b26a00' }}>{icon}</span>
      <span style={{ fontSize: 14, fontWeight: 700, color: '#173042', flex: 1 }}>{title}</span>
      <span style={{
        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
        background: ok ? '#e7f6ec' : '#fff4e0', color: ok ? '#1e7e46' : '#b26a00',
      }}>{ok ? 'OK' : 'Check'}</span>
    </div>
    {children}
  </div>
);

export const BackgroundJobs: React.FC = () => {
  const [data, setData] = useState<api.JobStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [reconcileDate, setReconcileDate] = useState(yesterdayUtc());

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      setData(await api.getJobStatus(reconcileDate));
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }, [reconcileDate]);

  useEffect(() => { load(); }, [load]);

  const q = data?.queue;
  const rc = data?.reconcile;
  const ua = data?.userActionArchive;
  const ch = data?.creditHistoryArchive;
  const pc = data?.priceCache;

  return (
    <div className="ov-page">
      <header className="ov-header">
        <div>
          <h1 className="ov-header__title">Background jobs</h1>
          <p className="ov-header__sub">
            Charging from the usage queue, the nightly checks and the S3 archives behind the credit system.
            {data && <> Checked {fmtWhen(data.checkedAtUtc)}.</>}
          </p>
        </div>
        <div className="ov-header__actions">
          <button className="btn-small btn-small--primary" onClick={load} disabled={loading}>
            <span className="material-icons">refresh</span> {loading ? 'Checking...' : 'Refresh'}
          </button>
        </div>
      </header>

      {err && (
        <div style={{ margin: '0 0 14px', padding: '9px 14px', borderRadius: 8, fontSize: 13, background: '#fdecea', color: '#c0392b', border: '1px solid #f5c6cb' }}>
          {err}
        </div>
      )}

      {/* What needs a look */}
      {data && (
        <section className="ov-section">
          {data.problems.length === 0 ? (
            <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, background: '#e7f6ec', borderColor: '#bfe6cd', color: '#1e7e46', fontSize: 13, fontWeight: 600 }}>
              <span className="material-icons">check_circle</span> All jobs look fine.
            </div>
          ) : (
            <div style={{ ...card, background: '#fff8e6', borderColor: '#f0c36d' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#8a5a00', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                <span className="material-icons">warning</span>
                {data.problems.length} thing{data.problems.length > 1 ? 's' : ''} to check
              </div>
              <ul style={{ margin: 0, paddingLeft: 22, color: '#5c4400', fontSize: 13, lineHeight: 1.7 }}>
                {data.problems.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}
        </section>
      )}

      {data && q && rc && ua && ch && pc && (
        <>
          <section className="ov-section">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>

              <Card title="Charging (usage queue)" icon="bolt" ok={q.pendingOver15Minutes === 0 && q.last24Hours.tenantNotFound === 0}>
                <Row label="Charged, last 24 hours" value={fmtInt(q.last24Hours.charged)} />
                <Row label="Not billable (no price or 0 credits)" value={fmtInt(q.last24Hours.notBillable)} />
                <Row label="Waiting to be charged" value={fmtInt(q.last24Hours.pending)} />
                <Row label="Waiting more than 15 minutes" value={fmtInt(q.pendingOver15Minutes)} />
                <Row label="No matching tenant" value={fmtInt(q.last24Hours.tenantNotFound)} />
                <Row label="Last action received" value={fmtWhen(q.lastActionAtUtc)} />
              </Card>

              <Card title="Nightly reconcile" icon="rule" ok={rc.mismatches === 0}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, margin: '0 0 6px' }}>
                  <span style={rowLabel}>Day checked (UTC)</span>
                  <input type="date" value={reconcileDate} max={yesterdayUtc()}
                         onChange={e => e.target.value && setReconcileDate(e.target.value)}
                         style={{ padding: '4px 8px', border: '1px solid #cdd6e0', borderRadius: 6, fontSize: 13 }} />
                </div>
                <Row label="Tenants with activity" value={fmtInt(rc.tenants)} />
                <Row label="Tenants that do not match" value={fmtInt(rc.mismatches)} />
                <p style={{ ...rowLabel, margin: '8px 0 0' }}>
                  Compares the daily usage summary with the stored actions and the credits taken on the ledger.
                </p>
              </Card>

              <Card title="Action archive (daily, S3)" icon="inventory_2"
                    ok={(!ua.nightlyEnabled || !ua.oldestWaitingUtc || toMs(ua.oldestWaitingUtc) > Date.now() - 3 * 86400000)
                        && !ua.daysBeingDeleted}>
                <Row label="Nightly run" value={ua.nightlyEnabled ? `On, ${ua.runAtUtc} UTC` : 'Off'} />
                <Row label="Actions waiting for the archive" value={fmtInt(ua.waitingRows)} />
                <Row label="Oldest waiting" value={ua.oldestWaitingUtc ? fmtWhen(ua.oldestWaitingUtc) : 'None'} />
                <Row label="Files written" value={fmtInt(ua.files)} />
                <Row label="Last file written" value={fmtWhen(ua.lastFileWrittenUtc)} />
                <Row label="Kept in the database from" value={ua.keptFromUtc ? fmtDay(ua.keptFromUtc) : '-'} />
                <Row label="Delete of old months" value={ua.deleteEnabled ? 'On' : 'Off (dry run)'} />
                <Row label="Days moved to S3" value={fmtInt(ua.daysDeleted)} />
                <Row label="Rows older than the kept month" value={fmtInt(ua.rowsOlderThanKept)} />
              </Card>

              <Card title="Credit history archive (monthly, S3)" icon="history"
                    ok={ch.monthsBeingDeleted === 0 && ch.rowsWithoutTenantGuid === 0}>
                <Row label="Nightly run" value={ch.nightlyEnabled ? `On, ${ch.runAtUtc} UTC` : 'Off'} />
                <Row label="Kept in the database from" value={monthLabel(ch.keptFromMonth)} />
                <Row label="Delete of old months" value={ch.deleteEnabled ? 'On' : 'Off (upload only)'} />
                <Row label="Files written" value={fmtInt(ch.files)} />
                <Row label="Months moved to S3" value={fmtInt(ch.deletedMonths)} />
                <Row label="Rows older than the kept months" value={fmtInt(ch.rowsOlderThanKept)} />
                <Row label="Last file written" value={fmtWhen(ch.lastFileWrittenUtc)} />
              </Card>

              <Card title="Price list cache" icon="sell" ok={!pc.lastError && pc.actions > 0}>
                <Row label="Actions in the cache" value={fmtInt(pc.actions)} />
                <Row label="Loaded" value={fmtWhen(pc.loadedAtUtc)} />
                {pc.lastError && <Row label="Last error" value={pc.lastError} />}
                <p style={{ ...rowLabel, margin: '8px 0 0' }}>
                  The charging Lambda keeps its own copy and reloads it every 10 minutes.
                </p>
              </Card>
            </div>
          </section>

          {rc.rows.length > 0 && (
            <section className="ov-section">
              <div className="ov-section__head">
                <h2 className="ov-section__title">Reconcile differences on {rc.dateUtc.slice(0, 10)}</h2>
              </div>
              <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e3e9ef', borderRadius: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                  <thead><tr>
                    <th style={th}>Tenant GUID</th>
                    <th style={th}>Actions (summary)</th><th style={th}>Actions (stored)</th>
                    <th style={th}>Credits (summary)</th><th style={th}>Credits (stored)</th><th style={th}>Credits (ledger)</th>
                  </tr></thead>
                  <tbody>
                    {rc.rows.map(r => (
                      <tr key={r.tenantGuid}>
                        <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{r.tenantGuid}</td>
                        <td style={td}>{fmtInt(r.aggregateCount)}</td>
                        <td style={td}>{fmtInt(r.userActionsCount)}</td>
                        <td style={td}>{fmtNum(r.aggregatePoints)}</td>
                        <td style={td}>{fmtNum(r.userActionsPoints)}</td>
                        <td style={td}>{fmtNum(r.ledgerPoints)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}

      {loading && !data && <p style={{ color: '#6b7684', fontSize: 13 }}>Checking the jobs...</p>}
    </div>
  );
};
